import { useNavigate } from "react-router-dom";
import { Mail, Phone, CreditCard, Car, Trash2, Edit2, AlertTriangle, CheckCircle, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import type { Driver } from "@/hooks/useVehicles";
import { format, differenceInDays, isPast } from "date-fns";
import { fr } from "date-fns/locale";

interface DriverCardProps {
  driver: Driver;
  vehiclesCount?: number;
  onEdit?: () => void;
  onDelete?: () => void;
  compact?: boolean;
}

export function DriverCard({ driver, vehiclesCount = 0, onEdit, onDelete, compact = false }: DriverCardProps) {
  const navigate = useNavigate();
  
  const initials = `${driver.first_name.charAt(0)}${driver.last_name.charAt(0)}`.toUpperCase();
  
  // License status calculation
  const getLicenseStatus = () => {
    if (!driver.license_expiry) return { status: "unknown", label: "Non renseigné", variant: "outline" as const };
    
    const expiryDate = new Date(driver.license_expiry);
    const daysUntilExpiry = differenceInDays(expiryDate, new Date());
    
    if (isPast(expiryDate)) {
      return { status: "expired", label: "Expiré", variant: "destructive" as const };
    }
    if (daysUntilExpiry <= 30) {
      return { status: "expiring", label: `Expire dans ${daysUntilExpiry}j`, variant: "secondary" as const };
    }
    return { status: "valid", label: "Valide", variant: "default" as const };
  };

  const licenseStatus = getLicenseStatus();

  const statusIcon = {
    expired: <AlertTriangle className="h-3.5 w-3.5" />,
    expiring: <Clock className="h-3.5 w-3.5" />,
    valid: <CheckCircle className="h-3.5 w-3.5" />,
    unknown: null,
  };

  const handleCardClick = () => {
    navigate(`/fleet/drivers/${driver.id}`);
  };

  const handleActionClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  if (compact) {
    return (
      <div 
        className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-xs bg-primary/10 text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium text-sm">
              {driver.first_name} {driver.last_name}
            </p>
            {driver.license_categories && driver.license_categories.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Permis: {driver.license_categories.join(", ")}
              </p>
            )}
          </div>
        </div>
        <Badge variant={licenseStatus.variant} className="text-xs gap-1">
          {statusIcon[licenseStatus.status]}
          {licenseStatus.label}
        </Badge>
      </div>
    );
  }

  return (
    <Card 
      className="group hover:shadow-md transition-all duration-200 cursor-pointer"
      onClick={handleCardClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="text-lg bg-primary/10 text-primary font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-foreground">
                {driver.first_name} {driver.last_name}
              </h3>
              <Badge variant={licenseStatus.variant} className="text-xs gap-1 mt-1">
                {statusIcon[licenseStatus.status]}
                Permis {licenseStatus.label}
              </Badge>
            </div>
          </div>
          
          {(onEdit || onDelete) && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onEdit && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8" 
                  onClick={(e) => handleActionClick(e, onEdit)}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
              )}
              {onDelete && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Supprimer ce conducteur ?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Cette action est irréversible. Le conducteur sera détaché de tous les véhicules.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                      <AlertDialogAction onClick={onDelete} className="bg-destructive hover:bg-destructive/90">
                        Supprimer
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mail className="h-4 w-4 shrink-0" />
            <span className="truncate">{driver.email}</span>
          </div>
          
          {driver.phone && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-4 w-4 shrink-0" />
              <span>{driver.phone}</span>
            </div>
          )}
          
          {driver.license_number && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <CreditCard className="h-4 w-4 shrink-0" />
              <span className="font-mono">{driver.license_number}</span>
            </div>
          )}
          
          {driver.license_expiry && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4 shrink-0" />
              <span>
                Expire le {format(new Date(driver.license_expiry), "d MMMM yyyy", { locale: fr })}
              </span>
            </div>
          )}
        </div>

        {/* License categories */}
        {driver.license_categories && driver.license_categories.length > 0 && (
          <div className="mt-3 pt-3 border-t">
            <div className="flex flex-wrap gap-1">
              {driver.license_categories.map((cat) => (
                <Badge key={cat} variant="outline" className="text-xs">
                  {cat}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Vehicles count */}
        <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Car className="h-4 w-4" />
            <span>{vehiclesCount} véhicule{vehiclesCount !== 1 ? "s" : ""} assigné{vehiclesCount !== 1 ? "s" : ""}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
